/**
 * DamageDice (Foundry: rule-element/damage-dice.ts): extra damage dice for the rolls of one or
 * more selectors, or an override of the base dice (a bigger die, another damage type).
 * Registered as a deferred constructor so each strike resolves them with its own options.
 */
import { createDamageDice, type DamageDice, type DamageDiceOverride } from "../damage-dice";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import { sluggify } from "../slug";
import type { EngineState } from "../state";
import { extractDamageAlterations, type DeferredDamageDiceOptions } from "../synthetics";
import { testPredicate } from "../predicate";
import { DAMAGE_CATEGORIES_UNIQUE, isDamageDieSize, isDamageType } from "../values";
import type { RuleHandler, RuleInstance } from "./base";

interface DamageDiceData {
  selectors: string[];
  diceNumber: unknown;
  dieSize: string | null;
  damageType: string | null;
  critical: boolean | null;
  category: "persistent" | "precision" | "splash" | null;
  tags: string[];
  override: Record<string, unknown> | null;
  hideIfDisabled: boolean;
  battleForm: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isValidOverride(override: unknown): boolean {
  if (override === undefined || override === null) return true;
  if (!isPlainObject(override)) return false;
  return (
    (typeof override.upgrade === "boolean" && !("downgrade" in override)) ||
    (typeof override.downgrade === "boolean" && !("upgrade" in override)) ||
    typeof override.damageType === "string" ||
    typeof override.dieSize === "string" ||
    typeof override.diceNumber === "string" ||
    (typeof override.diceNumber === "number" && Number.isInteger(override.diceNumber) && override.diceNumber >= 0 && override.diceNumber <= 256)
  );
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const raw = s.selector;
  const selectors = (Array.isArray(raw) ? raw : [raw]).filter((x): x is string => typeof x === "string" && x.length > 0);
  if (selectors.length === 0) return rule.fail("selector: must have at least 1");
  if (s.category !== undefined && s.category !== null && !(DAMAGE_CATEGORIES_UNIQUE as readonly string[]).includes(String(s.category))) return rule.fail(`category: "${String(s.category)}" is not persistent, precision or splash`);
  if (!isValidOverride(s.override)) {
    return rule.fail("The override property must be an object with one property of 'upgrade' (boolean), 'downgrade (boolean)', 'diceNumber' (integer between 0 and 10), 'dieSize' (d6-d12), or 'damageType' (recognized damage type)");
  }
  const dieSize = s.dieSize ?? s.diceSize ?? null;
  const data: DamageDiceData = {
    selectors,
    diceNumber: s.diceNumber === undefined || s.diceNumber === null ? null : cleanRuleValue(s.diceNumber),
    dieSize: typeof dieSize === "string" && dieSize ? dieSize : null,
    damageType: typeof s.damageType === "string" && s.damageType ? s.damageType : null,
    critical: typeof s.critical === "boolean" ? s.critical : null,
    category: (s.category as DamageDiceData["category"] | undefined) ?? null,
    tags: Array.isArray(s.tags) ? s.tags.filter((t): t is string => typeof t === "string").map((t) => sluggify(t)) : [],
    override: isPlainObject(s.override) ? structuredClone(s.override) : null,
    hideIfDisabled: s.hideIfDisabled === true,
    battleForm: s.battleForm === true,
  };
  rule.data = data as unknown as Record<string, unknown>;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as DamageDiceData;
  const selectors = data.selectors.map((s) => resolveInjectedProperties(rule, state, s)).filter((s) => !!s);
  if (rule.ignored) return;
  for (const selector of selectors) {
    if (selector === "null") continue;
    const deferred = (options: DeferredDamageDiceOptions): DamageDice | null => {
      const label = rule.reducedLabel;
      const predicate = resolveInjectedProperties(rule, state, structuredClone(rule.predicate), { warn: false });
      if (data.battleForm && !predicate.includes("battle-form")) predicate.push("battle-form");
      const parentOptions = rule.item.getRollOptions("parent");
      const testPassed = predicate.length === 0 || testPredicate(predicate, new Set([...(options.test ?? state.rollOptions.get(options.selectors)), ...parentOptions]));
      const resolveOptions = { resolvables: options.resolvables ?? {}, warn: testPassed };
      const diceNumber = data.diceNumber === null ? 0 : Number(resolveValue(rule, state, data.diceNumber, 0, resolveOptions)) || 0;
      if (rule.ignored) return null;
      const damageType = data.damageType ? resolveInjectedProperties(rule, state, data.damageType, { warn: testPassed }) : null;
      if (damageType !== null && !isDamageType(damageType)) {
        if (testPassed) rule.fail(`Unrecognized damage type: ${damageType}`);
        return null;
      }
      let override: DamageDiceOverride | null = null;
      if (data.override) {
        const raw = structuredClone(data.override);
        override = {};
        if (typeof raw.upgrade === "boolean") override.upgrade = raw.upgrade;
        if (typeof raw.downgrade === "boolean") override.downgrade = raw.downgrade;
        if ("damageType" in raw) {
          const type = typeof raw.damageType === "string" ? resolveInjectedProperties(rule, state, raw.damageType, { warn: testPassed }) : raw.damageType;
          if (!isDamageType(type)) {
            if (testPassed) rule.fail("Unrecognized damage type in override");
            return null;
          }
          override.damageType = type;
        }
        if ("diceNumber" in raw && raw.diceNumber !== undefined && raw.diceNumber !== null) {
          const n = Math.floor(Number(resolveValue(rule, state, raw.diceNumber, NaN, resolveOptions)));
          if (Number.isNaN(n)) return null;
          if (n < 0) {
            if (testPassed) rule.fail("A dice number must resolve to at least zero");
            return null;
          }
          override.diceNumber = n;
        }
        if ("dieSize" in raw) {
          const size = typeof raw.dieSize === "string" ? resolveInjectedProperties(rule, state, raw.dieSize, { warn: testPassed }) : raw.dieSize;
          if (!isDamageDieSize(size)) {
            if (testPassed) rule.fail("Unrecognized die size in override");
            return null;
          }
          override.dieSize = size;
        }
      }
      const dieSize = data.dieSize ? resolveInjectedProperties(rule, state, data.dieSize, { warn: testPassed }) : null;
      if (dieSize !== null && !isDamageDieSize(dieSize)) {
        if (testPassed) rule.fail("Die size must be a recognized damage die size, null, or omitted");
        return null;
      }
      const slug = rule.slug ?? sluggify(rule.item.name);
      return createDamageDice({
        selector,
        slug,
        label,
        diceNumber,
        dieSize,
        critical: data.critical,
        category: data.category,
        damageType,
        predicate,
        parentOptions,
        tags: data.tags,
        override,
        enabled: testPassed,
        hideIfDisabled: data.hideIfDisabled,
        alterations: extractDamageAlterations(state.synthetics.damageAlterations, options.selectors, slug),
        source: rule.item.uuid,
        sourceName: rule.item.name,
      });
    };
    (state.synthetics.damageDice[selector] ??= []).push(deferred);
  }
}

export const DamageDiceHandler: RuleHandler = { key: "DamageDice", parse, beforePrepareData };
